/** Shared SEO helpers for BlogForm (Approach A — dirty-flag sync). */

export const emptySeoForm = {
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  canonical_url: "",
  robots: "index,follow",
  slug: "",
  og_type: "article",
  og_title: "",
  og_description: "",
  og_image: "",
  og_image_alt: "",
};

export const emptySeoDirty = {
  meta_title: false,
  meta_description: false,
  meta_keywords: false,
  canonical_url: false,
  slug: false,
  og_title: false,
  og_description: false,
  og_image_alt: false,
  header_image_title: false,
  header_image_alt: false,
};

export const SEO_ROBOTS_OPTIONS = [
  { value: "index,follow", label: "Index, Follow" },
  { value: "noindex,follow", label: "No Index, Follow" },
  { value: "index,nofollow", label: "Index, No Follow" },
  { value: "noindex,nofollow", label: "No Index, No Follow" },
];

export function withTrailingSlash(slug) {
  const trimmed = String(slug || "")
    .trim()
    .replace(/^\/+/, "");
  if (!trimmed) return "";
  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
}

/**
 * Public website origin for canonical URLs.
 * Override with REACT_APP_WEBSITE_URL; otherwise map from API host.
 * Example: https://gharplans.pk/best-modern-bedroom-ideas-for-small-rooms-in-pakistan-2025/
 */
export function getWebsiteBaseUrl() {
  const explicit = String(process.env.REACT_APP_WEBSITE_URL || "")
    .trim()
    .replace(/\/+$/, "");
  if (explicit) return explicit;

  const api = String(process.env.REACT_APP_BASE_URL || "").toLowerCase();
  if (api.includes("api-stg.gharplans.pk")) return "https://stg.gharplans.pk";
  if (api.includes("api-dev.gharplans.pk")) return "https://dev.gharplans.pk";
  if (api.includes("test-api.gharplans.pk")) return "https://test.gharplans.pk";
  if (api.includes("localhost") || api.includes("127.0.0.1")) {
    return "http://localhost:3000";
  }
  return "https://gharplans.pk";
}

/** `https://gharplans.pk/{slug}/` */
export function buildCanonicalUrl(slugOrCustomUrl) {
  const slug = withTrailingSlash(slugOrCustomUrl);
  if (!slug) return "";
  return `${getWebsiteBaseUrl()}/${slug}`;
}

/** Prefer `/images/...` path when a full CDN/API URL is given. */
export function toSeoImagePath(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    return "";
  }
  if (trimmed.startsWith("/images/")) return trimmed;
  const match = trimmed.match(/\/images\/[^?#]+/);
  return match ? match[0] : trimmed;
}

export function buildSeoDirtyFromSources({
  seo,
  customUrl,
}) {
  const slugFromUrl = withTrailingSlash(customUrl);
  const canonicalFromUrl = buildCanonicalUrl(customUrl);
  const metaDes = seo?.meta_description || "";

  // Title-derived fields stay unlocked — Name is master and force-syncs them.
  return {
    meta_title: false,
    meta_description: Boolean(metaDes),
    meta_keywords: Boolean(seo?.meta_keywords),
    canonical_url: Boolean(
      seo?.canonical_url && seo.canonical_url !== canonicalFromUrl,
    ),
    slug: Boolean(seo?.slug && withTrailingSlash(seo.slug) !== slugFromUrl),
    og_title: false,
    og_description: Boolean(
      seo?.og_description && seo.og_description !== metaDes,
    ),
    og_image_alt: false,
    header_image_title: false,
    header_image_alt: false,
  };
}

export function seoFormFromEntity(seo = {}) {
  return {
    meta_title: seo.meta_title || "",
    meta_description: seo.meta_description || "",
    meta_keywords: seo.meta_keywords || "",
    canonical_url: seo.canonical_url || "",
    robots: seo.robots || "index,follow",
    slug: withTrailingSlash(seo.slug || ""),
    og_type: seo.og_type || "article",
    og_title: seo.og_title || "",
    og_description: seo.og_description || "",
    og_image: seo.og_image || "",
    og_image_alt: seo.og_image_alt || "",
  };
}

export const REQUIRED_SEO_FIELDS = [
  { key: "meta_title", label: "Meta Title" },
  { key: "meta_description", label: "Meta Description" },
  { key: "meta_keywords", label: "Meta Keywords" },
  { key: "slug", label: "Slug" },
  { key: "canonical_url", label: "Canonical URL" },
  { key: "robots", label: "Robots" },
  { key: "og_type", label: "OG Type" },
];

export function validateSeoForm(seoForm) {
  const errors = {};
  REQUIRED_SEO_FIELDS.forEach(({ key, label }) => {
    if (!String(seoForm[key] || "").trim()) {
      errors[key] = `${label} is required`;
    }
  });
  return errors;
}

export function buildSeoFormData({
  seoForm,
  entityId,
  ogImageFile,
  fallbackOgImagePath,
}) {
  const fd = new FormData();
  fd.append("entity_type", "blog");
  fd.append("entity_id", String(entityId));
  fd.append("meta_title", String(seoForm.meta_title || "").trim());
  fd.append("meta_description", String(seoForm.meta_description || "").trim());
  fd.append("meta_keywords", String(seoForm.meta_keywords || "").trim());
  fd.append("canonical_url", String(seoForm.canonical_url || "").trim());
  fd.append("robots", String(seoForm.robots || "index,follow").trim());
  fd.append("slug", withTrailingSlash(seoForm.slug));
  fd.append("og_type", String(seoForm.og_type || "article").trim());
  if (seoForm.og_title) fd.append("og_title", String(seoForm.og_title).trim());
  if (seoForm.og_description) {
    fd.append("og_description", String(seoForm.og_description).trim());
  }
  if (seoForm.og_image_alt) {
    fd.append("og_image_alt", String(seoForm.og_image_alt).trim());
  }
  if (ogImageFile) {
    fd.append("og_image", ogImageFile);
  } else if (fallbackOgImagePath) {
    fd.append("og_image", fallbackOgImagePath);
  }
  return fd;
}

/** Force-sync blog title into every section image alt/title (title is master). */
export function applyTitleToSectionBlocks(blocks, title) {
  return (blocks || []).map((block) => ({
    ...block,
    imageAlt: title,
    imageTitle: title,
    _imageAltDirty: false,
    _imageTitleDirty: false,
  }));
}

/** Mark a section image SEO field as manually edited (until next title change). */
export function markSectionImageField(block, field, value) {
  if (field === "imageAlt") {
    return { ...block, imageAlt: value, _imageAltDirty: true };
  }
  if (field === "imageTitle") {
    return { ...block, imageTitle: value, _imageTitleDirty: true };
  }
  return { ...block, [field]: value };
}

/**
 * On edit load: keep values as-is; title remains master on next Name change.
 * Dirty flags start unlocked so temporary edits work until title changes.
 */
export function hydrateSectionImageDirtyFlags(blocks) {
  return (blocks || []).map((block) => ({
    ...block,
    _imageAltDirty: false,
    _imageTitleDirty: false,
  }));
}

/** New content section prefilled from current blog title. */
export function createEmptyContentBlock(title = "") {
  return {
    text: "",
    text_ur: "",
    textAlignment: "left",
    image: null,
    imageAlignment: "right",
    imageAlt: title || "",
    imageTitle: title || "",
    _imageAltDirty: false,
    _imageTitleDirty: false,
  };
}

/** After image upload: always fill alt/title from current blog title. */
export function applyTitleOnSectionImageUpload(block, title) {
  return {
    ...block,
    imageAlt: title,
    imageTitle: title,
    _imageAltDirty: false,
    _imageTitleDirty: false,
  };
}

/**
 * Title-is-master: Name change always overwrites meta/OG title fields,
 * even if they were customized. Returns dirty keys to clear.
 */
export function applyBlogTitleToSeoForm({ seoPrev, title }) {
  return {
    nextSeo: {
      ...seoPrev,
      meta_title: title,
      og_title: title,
      og_image_alt: title,
    },
    dirtyKeysToClear: ["meta_title", "og_title", "og_image_alt"],
  };
}

/** Header image SEO always follows blog title. */
export function applyBlogTitleToHeaderSeo(formPrev, title) {
  return {
    ...formPrev,
    name: title,
    header_image_title: title,
    header_image_alt: title,
  };
}
